<template>
  <div>
    <div class="container my-5">
      <h2 class="mb-4 text-center">Shopping Cart</h2>

      <!-- EMPTY CART -->
      <div v-if="cartItems.length === 0" class="text-center py-5">
        <img src="/images/empty-cart.png" alt="Empty Cart" width="150" class="mb-3" />
        <h4>Your cart is empty</h4>
        <p class="text-muted">Looks like you haven't added anything yet.</p>
        <button class="btn btn-primary mt-3" @click="$router.push('/')">Continue Shopping</button>
      </div>

      <div class="row" v-else>
        <!-- Cart Items -->
        <div class="col-lg-8">
          <div class="card shadow-sm mb-4">
            <div class="card-body">
              <div class="cart-item" v-for="cart in cartItems" :key="cart._id">
                <div class="cart-left">
                  <img :src="cart.mainImage" class="cart-img" />
                  <div class="cart-name">
                    <h6>{{ cart.productDetail.name }}</h6>
                  </div>
                </div>

                <div class="cart-center">
                  <input type="number" min="1" v-model="cart.qty" class="qty-input" @change="updateCartQty(cart)" />
                  <span class="item-price">₹{{ cart.itemPrice }}</span>
                </div>

                <button class="delete-btn" @click="deleteCartItem(cart._id)">
                  <i class="fa fa-trash" />
                </button>
              </div>
            </div>
          </div>
        </div>

        <!-- Cart Summary -->
        <div class="col-lg-4">
          <div class="card shadow-sm">
            <div class="card-body">
              <h5 class="mb-3">Order Summary</h5>

              <div class="d-flex justify-content-between mb-2">
                <span>Subtotal</span>
                <span>₹{{ subTotal }}</span>
              </div>
              <div class="d-flex justify-content-between mb-2">
                <span>Shipping</span>
                <span>₹0</span>
              </div>
              <div class="d-flex justify-content-between fw-bold border-top pt-2">
                <span>Total</span>
                <span>₹{{ subTotal }}</span>
              </div>

              <!-- Desktop action buttons -->
              <div class="d-none d-md-flex flex-column gap-2 mt-4">
                <button class="btn btn-whatsapp w-100" @click="orderOnWhatsapp()">
                  <svg xmlns="http://www.w3.org/2000/svg" width="15" height="15" viewBox="0 0 24 24" fill="currentColor" style="vertical-align:-2px; margin-right:5px;"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347z"/><path d="M12 0C5.373 0 0 5.373 0 12c0 2.117.549 4.107 1.51 5.833L.057 23.535a.75.75 0 0 0 .916.927l5.834-1.53A11.945 11.945 0 0 0 12 24c6.627 0 12-5.373 12-12S18.627 0 12 0zm0 21.75a9.708 9.708 0 0 1-4.95-1.355l-.355-.211-3.666.962.978-3.58-.231-.368A9.715 9.715 0 0 1 2.25 12C2.25 6.615 6.615 2.25 12 2.25S21.75 6.615 21.75 12 17.385 21.75 12 21.75z"/></svg>
                  Order on WhatsApp
                </button>
                <button class="btn btn-cart w-100" @click="proceedToCheckout()">
                  <i class="fa fa-shopping-bag me-2" />
                  Proceed to Checkout
                </button>
              </div>

            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- ✅ Mobile bar — inline below Order Summary, above footer -->
    <div class="mobile-cart-bar d-flex d-md-none" v-if="cartItems.length > 0">
      <div class="cart-bar-total">
        <span class="bar-label">Total</span>
        <span class="bar-amount">₹{{ subTotal }}</span>
      </div>
      <div class="cart-bar-actions">
        <button class="btn btn-whatsapp" @click="orderOnWhatsapp()">
          <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="currentColor" style="vertical-align:-2px; margin-right:3px;"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347z"/><path d="M12 0C5.373 0 0 5.373 0 12c0 2.117.549 4.107 1.51 5.833L.057 23.535a.75.75 0 0 0 .916.927l5.834-1.53A11.945 11.945 0 0 0 12 24c6.627 0 12-5.373 12-12S18.627 0 12 0zm0 21.75a9.708 9.708 0 0 1-4.95-1.355l-.355-.211-3.666.962.978-3.58-.231-.368A9.715 9.715 0 0 1 2.25 12C2.25 6.615 6.615 2.25 12 2.25S21.75 6.615 21.75 12 17.385 21.75 12 21.75z"/></svg>
          WhatsApp
        </button>
        <button class="btn btn-cart" @click="proceedToCheckout()">
          Checkout
        </button>
      </div>
    </div>

  </div>
</template>

<script>
import { useToast } from 'vue-toast-notification';
const $toast = useToast();
import { VueTelInput } from 'vue-tel-input';
import 'vue-tel-input/vue-tel-input.css';
import ProductService from "../services/ProductService";
import OrderService from "../services/OrderService";
import { EventBus } from '@/eventBus'

const WHATSAPP_NUMBER = "919512981118"; // 🔁 Replace with your number

export default {
  name: 'Cart',
  components: { VueTelInput },
  data() {
    return {
      isDisabled: false,
      cartItems: [],
      subTotal: 0,
    };
  },
  methods: {
    getCartItems() {
      ProductService.getCartItems({ slug: this.slug })
        .then((response) => {
          this.cartItems = response.data.data;
          this.subTotal = response.data.subTotal;
        }).catch((e) => {
          $toast.error(e.response.data.respones_msg);
        });
    },

    orderOnWhatsapp() {
      if (!this.cartItems || this.cartItems.length === 0) return;

      const itemLines = this.cartItems
        .map(c => `  - ${c.productDetail.name} x${c.qty} = Rs.${c.itemPrice}`)
        .join('\n');

      const message =
        `Hi! I'd like to place an order\n\n` +
        `*Items:*\n${itemLines}\n\n` +
        `*Total:* Rs.${this.subTotal}\n\n` +
        `Please confirm availability and share payment details. Thank you!`;

      window.open(`https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(message)}`, '_blank');
    },

    async updateCartQty(cart) {
      if (cart.qty <= 0) cart.qty = 1;
      let data = { cartId: cart._id, qty: cart.qty };
      await ProductService.updateCartQty(data)
        .then((response) => {
          EventBus.emit('cart-updated');
          $toast.success(response.data.respones_msg);
          this.getCartItems();
        }).catch((e) => {
          $toast.error(e.response.data.respones_msg);
        });
    },

    proceedToCheckout() {
      if (!this.cartItems || this.cartItems.length === 0) {
        $toast.error('Your cart is empty');
        return;
      }
      this.$router.push({ name: 'Checkout' });
    },

    async createOrder() {
      let data = { amount: 500 };
      const response = await OrderService.createOrder(data);
      const order = response.data.data;
      const options = {
        key: process.env.VUE_APP_RAZORPAY_KEY,
        amount: order.amount,
        currency: order.currency,
        name: "Bees Atelier",
        description: "Fixed Payment",
        order_id: order.id,
        handler: async (paymentResponse) => {
          try {
            await OrderService.verifyPayment({
              razorpay_order_id: paymentResponse.razorpay_order_id,
              razorpay_payment_id: paymentResponse.razorpay_payment_id,
              razorpay_signature: paymentResponse.razorpay_signature,
            });
            this.$toast.success("Payment successful");
          } catch (err) {
            this.$toast.error("Payment verification failed");
          }
        },
      };
      const rzp = new Razorpay(options);
      rzp.on("payment.failed", (response) => { console.error(response.error); });
      rzp.open();
    },

    async deleteCartItem(cartId) {
      await ProductService.deleteCartItem({ cartId })
        .then((response) => {
          EventBus.emit('cart-updated');
          $toast.success(response.data.respones_msg);
          this.getCartItems();
        }).catch((e) => {
          $toast.error(e.response.data.respones_msg);
        });
    },
  },
  beforeMount() {
    this.getCartItems();
  },
}
</script>

<style scoped>
/* ── Desktop layout ── */
.cart-item {
  display: grid;
  grid-template-columns: 1fr auto auto;
  align-items: center;
  padding: 15px;
  border-bottom: 1px solid #eee;
  gap: 15px;
}

.cart-left {
  display: flex;
  align-items: center;
  gap: 12px;
}

.cart-img {
  width: 70px;
  height: 70px;
  border-radius: 8px;
  object-fit: cover;
}

.cart-center {
  display: flex;
  align-items: center;
  gap: 12px;
}

.delete-btn {
  border: none;
  background: transparent;
  font-size: 20px;
  color: #d9534f;
}

/* ── Shared button base ── */
.btn-cart,
.btn-whatsapp {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  height: 44px;
  padding: 0 20px;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 500;
  border: none;
  cursor: pointer;
  transition: opacity 0.15s ease;
  white-space: nowrap;
}

.btn-cart,
.btn-cart:hover {
  background: linear-gradient(135deg, rgb(118, 172, 175), rgb(98, 152, 155));
  color: white;
}

.btn-whatsapp {
  background-color: #25D366;
  color: #ffffff;
}

.btn-whatsapp:hover {
  background-color: #1ebe57;
  color: #ffffff;
  opacity: 1;
}

/* ── Mobile sticky bar ── */
/* ── Mobile bar — sits in normal flow, above footer ── */
.mobile-cart-bar {
  display: flex;
  background: #ffffff;
  border-top: 2px solid #dee2e6;
  border-bottom: 2px solid #dee2e6;
  padding: 14px 16px;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  margin-top: 16px;         /* breathing room below Order Summary */
  margin-bottom: 8px;
}

.cart-bar-total {
  display: flex;
  flex-direction: column;
  line-height: 1.2;
  min-width: 70px;
}

.bar-label {
  font-size: 11px;
  color: #888;
}

.bar-amount {
  font-size: 17px;
  font-weight: 700;
  color: #111;
}

.cart-bar-actions {
  display: flex;
  gap: 8px;
  flex: 1;
  justify-content: flex-end;
}

.cart-bar-actions .btn-cart,
.cart-bar-actions .btn-whatsapp {
  font-size: 13px;
  padding: 0 14px;
  height: 42px;
}

/* ── Mobile cart item layout ── */
@media (max-width: 576px) {
  .container {
    padding-bottom: 16px; 
  }

  .cart-item {
    grid-template-columns: 1fr;
    gap: 5px;
    padding: 12px 8px;
    position: relative;
  }

  .cart-left { gap: 10px; }

  .cart-img {
    width: 55px;
    height: 55px;
  }

  .cart-center {
    display: flex;
    width: 100%;
    justify-content: space-between;
    padding-left: 65px;
  }

  .qty-input {
    width: 55px !important;
    height: 32px;
  }

  .item-price {
    font-size: 15px;
    font-weight: bold;
  }

  .delete-btn {
    position: absolute;
    right: 10px;
    margin-top: -25px;
  }
}

@media (min-width: 577px) {
  .qty-input {
    width: 55px !important;
    padding: 4px 6px;
  }
}
</style>